// SCRUM-434: what the recipient actually receives is longer than what the
// operator types — `{app}` is 19 characters, not 5, and `{event}` can be 33.
// Counting the template made an operator believe a message fit one SMS
// segment when it did not, and every recipient paid for the second.
// Mirrors the API's campaign-template.ts (varsFor / render / withContactLines).

export const APP_LINK = 'https://arnd.io/app';

/** SCRUM-449: what `{app}` becomes — the event's own link with the channel
 * as its own path segment when the event has a slug. Mirrors the API's
 * appLink. Supersedes the earlier `?src=` query-param format. */
export function appLink(slug, channel) {
  return slug ? `${APP_LINK}/${slug}/${channel}` : APP_LINK;
}
/** SCRUM-449: true when every stage in a campaign's audience is `lead`
 * ("Signed up"). Used to size the STOP-only contact line (a lead's text
 * names no event, so it never gets the MUTE line) and, with no event known,
 * the `lead` flag on the worst-case context. Leads get the same `{app}` as
 * everyone else now (the event's own link, or the worst-case slug link on
 * a playbook campaign) — see `campaignSmsContext`. Mirrors the API's
 * per-recipient stage check. */
export const isLeadOnly = (stages = []) => stages.length > 0 && stages.every((s) => s === 'lead');

/** SCRUM-449: `{app}` when the event is not known — the link with a
 * 15-character slug, the longest the counter assumes. */
export const WORST_APP_LINK = `${APP_LINK}/${'x'.repeat(15)}/sms`;
/** SCRUM-449: a first text about an event to a number already texted before.
 * Must equal the API's MUTE_LINE character for character, or the counter lies. */
export const MUTE_LINE = ' Reply MUTE to stop texts about this event.';
/** SCRUM-449: a parent's first-ever text — one line for both opt-outs,
 * replacing MUTE + STOP. Must equal the API's MUTE_STOP_LINE. */
export const MUTE_STOP_LINE = ' Reply MUTE (this event) or STOP (all).';
/** The API's STOP_LINE: a first-ever text whose body already ends with it
 * carries the opt-out, so MUTE_STOP_LINE is not appended. */
export const STOP_LINE = ' Reply STOP to opt out.';
/** The longest `{deadline}` the API's deadlinePhrase produces — the
 * counter's stand-in when no real deadline is known. */
export const WORST_DEADLINE = 'Wednesday at midnight';
/** `{count}` when there is no audience to measure: three digits. */
export const WORST_COUNT = '125';
/** `{athlete}`/`{parent}` when no real name is available: a long
 * (12-character) first name. Shared fallback — see worstCaseVars. */
export const WORST_ATHLETE = 'Christabelle';
/** SCRUM-449: `{event}` on a template with no real event behind it (the
 * Campaigns playbook editor) — the longest realistic event name the counter
 * assumes, 40 characters. A real event (AudienceBuilder, CampaignTimeline)
 * always passes its own name instead; this is only for the page that has
 * none to give. */
export const WORST_EVENT_NAME = 'x'.repeat(40);

/** SCRUM-449 (leads-use-the-event's-link follow-up): the SMS length context
 * an automatic campaign is sized on — the SAME context the API's save check
 * uses, so the admin and the API agree on "fits in one text":
 * - a per-event override (`event` given) always uses the event's real name
 *   and its own `/<slug>/sms` link (the generic link when it has no slug) —
 *   lead recipients get that same link now too, not a separate generic one;
 * - a playbook campaign (no event) keeps the worst case: a 40-character
 *   event name and the 15-character-slug worst-case link, whether or not
 *   the campaign is lead-only (a playbook never knows if the real event
 *   behind it will have a slug, so it can't assume the shorter link). */
export function campaignSmsContext(stages = [], event = null) {
  const lead = isLeadOnly(stages);
  if (event) {
    return { parents: [], eventName: event.name ?? '', app: appLink(event.slug, 'sms'), lead };
  }
  return { parents: [], eventName: WORST_EVENT_NAME, app: WORST_APP_LINK, lead };
}

/** SCRUM-449: what the API's real send falls back {athlete} to — no athlete
 * named, or (new) a named athlete whose first name still ran the SMS over
 * one segment. Mirrors the API's varsFor default exactly. */
export const ATHLETE_FALLBACK = 'your athlete';
/** GSM-7 single-message limit, and the UCS-2 one. */
export const SMS_LIMIT = 160;
export const SMS_UNICODE_LIMIT = 70;
export const NO_COUNT_WORD = 'the';

export function render(template, vars) {
  return String(template ?? '').replace(/\{(\w+)\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : '?'
  );
}

const firstName = (full) => String(full ?? '').trim().split(/\s+/)[0];

/** SCRUM-449: `{athlete}` is first names only, on every channel — "Aaliyah",
 * "Ava and Mia". Mirrors the API's athlete rendering (no more per-channel
 * split — push and email used to keep full names; they don't any more). */
export function athleteFirstNames(athletes = []) {
  return (athletes ?? []).map((a) => firstName(a?.fullName)).filter(Boolean).join(' and ');
}

/** The placeholder values that make the SMS longest across this audience:
 * the longest parent first name, the longest first-name athlete list, the
 * biggest photo count. `{parent}`/`{athlete}`/`{count}` vary per recipient,
 * so the counter shows the worst case — the only number that answers "will
 * this fit?". `{event}` is passed through as given — a real event passes its
 * own name; a template with no real event (Campaigns) passes WORST_EVENT_NAME.
 * With no audience: "Christabelle" for both parent and athlete, "125",
 * "Wednesday at midnight" and a 15-character slug link. SCRUM-449: `parent`
 * is the longest REAL first name in the audience (like `athlete` already
 * was) — not a fixed word that a short real name would never beat. */
export function worstCaseVars(parents = [], eventName = '', app = WORST_APP_LINK, deadline = WORST_DEADLINE) {
  const list = parents ?? [];
  let parentName = '';
  let athlete = '';
  let count = list.length ? NO_COUNT_WORD : WORST_COUNT;
  for (const p of list) {
    const first = firstName(p?.fullName);
    if (first && first.length > parentName.length) parentName = first;
    const joined = athleteFirstNames(p?.athletes);
    if (joined.length > athlete.length) athlete = joined;
    const n = (p?.athletes ?? []).reduce((sum, a) => sum + (a?.matchedPhotos ?? 0), 0);
    if (n > 0 && String(n).length > String(count).length) count = n;
  }
  return { parent: parentName || WORST_ATHLETE, athlete: athlete || WORST_ATHLETE, count, event: eventName, app, deadline };
}

/** GSM-7 packs 160 per message (153 per part when split); anything outside
 * it makes the whole message UCS-2 at 70 (67 per part). Emoji take two
 * UCS-2 units. Same rule as the API's smsSegments. */
function smsUnits(body) {
  const text = String(body ?? '');
  // eslint-disable-next-line no-control-regex
  const unicode = /[^\u0000-\u007F -ÿ€]/.test(text);
  const units = unicode
    ? [...text].reduce((n, ch) => n + (ch.codePointAt(0) > 0xffff ? 2 : 1), 0)
    : text.length;
  return { unicode, units, limit: unicode ? SMS_UNICODE_LIMIT : SMS_LIMIT };
}

export function smsSegments(body) {
  if (!String(body ?? '')) return 1;
  const { unicode, units, limit } = smsUnits(body);
  return units <= limit ? 1 : Math.ceil(units / (unicode ? 67 : 153));
}

/** How far past one text a body runs: 0 when it fits. */
export function smsOverBy(body) {
  const { units, limit } = smsUnits(body);
  return Math.max(0, units - limit);
}

/** A rendered SMS body's length/segments/overage/limit, in the shape every
 * length-checking function here returns. */
function measure(text) {
  return { text, length: text.length, segments: smsSegments(text), over: smsOverBy(text), limit: smsUnits(text).limit };
}

/** The API's withContactLines: a parent's first-ever text gets the one
 * combined MUTE/STOP line; a first text about this event to a number already
 * texted gets the MUTE line; otherwise nothing. Never doubles a line the
 * author already typed — and a first-ever text already ending in the plain
 * STOP line counts as carrying its opt-out (API campaign-template.ts).
 *
 * SCRUM-449: a `lead` recipient ("Signed up") gets only the plain
 * STOP line on their first-ever text, and never a MUTE line — their text
 * names no event to mute. */
export function withContactLines(body, { firstContact = false, firstForEvent = false, lead = false } = {}) {
  const out = String(body ?? '').trimEnd();
  if (lead) {
    if (!firstContact) return body;
    return out.endsWith(STOP_LINE.trim()) ? out : out + STOP_LINE;
  }
  if (firstContact) {
    return out.endsWith(MUTE_STOP_LINE.trim()) || out.endsWith(STOP_LINE.trim()) ? out : out + MUTE_STOP_LINE;
  }
  if (firstForEvent) return out.endsWith(MUTE_LINE.trim()) ? out : out + MUTE_LINE;
  return body;
}

/** Longest rendered body for this template across the audience, with the
 * contact line when it will be appended. `over` is how many characters past
 * one text it runs (0 = fits). */
export function worstCaseLength(
  template,
  parents,
  eventName,
  { firstContact = false, firstForEvent = false, app = WORST_APP_LINK, deadline, lead = false } = {}
) {
  const body = render(template, worstCaseVars(parents, eventName, app || WORST_APP_LINK, deadline || WORST_DEADLINE));
  const text = withContactLines(body, { firstContact, firstForEvent, lead });
  return measure(text);
}

/** SCRUM-449: the worst case every SMS must fit in one text — a parent's
 * first-ever contact, so the combined MUTE/STOP line is on it (or, for a
 * lead-only template, the plain STOP line — `ctx.lead`). */
export function worstCaseSms(template, { parents = [], eventName = '', app, deadline, lead = false } = {}) {
  return worstCaseLength(template, parents, eventName, { firstContact: true, app, deadline, lead });
}

/** One real parent's SMS vars — the API's actual send-time render, not a
 * worst-case placeholder: `{parent}` is their own first name ("there" if
 * unnamed, matching the API's varsFor), `{count}` their own matched-photo
 * total. `athlete` is passed in so the caller can try the real name first
 * and retry with ATHLETE_FALLBACK. */
function realParentVars(p, eventName, app, deadline, athlete) {
  const n = (p?.athletes ?? []).reduce((sum, a) => sum + (a?.matchedPhotos ?? 0), 0);
  // SCRUM-449 follow-up: a lead recipient gets the same {app} as everyone
  // else now — the event's own link (or the generic one, exactly as `app`
  // already resolves it) — not a forced-generic override. Only the contact
  // line still varies per stage (withContactLines' `lead` flag, below).
  return { parent: firstName(p?.fullName) || 'there', athlete, count: n > 0 ? n : NO_COUNT_WORD, event: eventName, app, deadline };
}

function renderParentSms(template, p, eventName, app, deadline, athlete, firstContact) {
  const body = render(template, realParentVars(p, eventName, app, deadline, athlete));
  return measure(withContactLines(body, { firstContact, lead: p?.stage === 'lead' }));
}

/**
 * SCRUM-449: the API's real send-time SMS semantics — not one audience-wide
 * worst case. For each real parent, `{athlete}` renders with their athlete's
 * first name(s); when that alone runs the SMS over one segment, the API
 * re-renders that parent's message with `{athlete}` = ATHLETE_FALLBACK
 * ("your athlete") instead — the same fallback it already uses for a parent
 * with no athlete named at all. The template is INVALID only when a parent
 * is still over one segment after that fallback (a parent with no athlete
 * named starts at the fallback, so it's invalid there directly, with no
 * second render to try).
 *
 * With no parents (the filtered audience is currently empty), there is no
 * real data to render — the check falls back to the generic worst case
 * (worstCaseVars: real event name, "Christabelle" parent), but with
 * `{athlete}` forced to ATHLETE_FALLBACK, since that's the one guarantee
 * that must hold no matter who ends up in the audience.
 *
 * A lead-stage parent (`p.stage === 'lead'`) gets the same `{app}` as every
 * other recipient (the event's own link when it has a slug, the generic
 * link otherwise) and only the plain STOP line on their first-ever text
 * (never MUTE — their text names no event) — the API does the same. With
 * no parents, `ctx.lead` (from `campaignSmsContext`'s `isLeadOnly`) stands
 * in for the audience's own `p.stage`.
 *
 * Returns `{ invalid, fallbackCount, worst }`: `fallbackCount` is how many
 * parents needed the "your athlete" swap to fit (for the informational
 * banner); `worst` is the longest rendering actually sent (post-fallback),
 * in the same shape as worstCaseLength/worstCaseSms.
 */
export function audienceSmsCheck(template, { parents = [], eventName = '', app = WORST_APP_LINK, deadline = WORST_DEADLINE, lead = false } = {}) {
  const list = parents ?? [];
  if (list.length === 0) {
    const vars = { ...worstCaseVars([], eventName, app, deadline), athlete: ATHLETE_FALLBACK };
    const worst = measure(withContactLines(render(template, vars), { firstContact: true, lead }));
    return { invalid: worst.segments > 1, fallbackCount: 0, worst };
  }
  let fallbackCount = 0;
  let invalid = false;
  let worst = null;
  for (const p of list) {
    const namedAthlete = athleteFirstNames(p?.athletes);
    let result = renderParentSms(template, p, eventName, app, deadline, namedAthlete || ATHLETE_FALLBACK, true);
    if (result.segments > 1) {
      if (namedAthlete) {
        fallbackCount += 1;
        result = renderParentSms(template, p, eventName, app, deadline, ATHLETE_FALLBACK, true);
      }
      if (result.segments > 1) invalid = true;
    }
    if (!worst || result.length > worst.length) worst = result;
  }
  return { invalid, fallbackCount, worst };
}
